import { SignalBinding } from "signals";
import { SkipSpinCommand } from "slotworks/commands/comms/skip-spin-command";
import { StopSpinCommand } from "slotworks/commands/comms/stop-spin-command";
import Components from "slotworks/components/components";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import GameLoop from "slotworks/core/game-loop";
import GameState from "slotworks/model/game-state";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import SettingsModel from "slotworks/model/settings/settings-model";
import SlotDefinition from "slotworks/model/slot-definition";
import SymbolDefinition from "slotworks/model/symbol-definition";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import SmartUIFlags from "slotworks/smart-ui/smart-ui-flags";
import SmartUIManager from "slotworks/smart-ui/smart-ui-manager";
import State from "slotworks/state-machine/states/state";
import Logger from "slotworks/utils/logger";

export default class Spin extends State {

    public static ENABLE_TURBO_PROMPT: boolean = false;
    public static ENABLE_SAFETY_CHECK: boolean = true;
    public static TURBO_PROMPT_COUNT: number = 5;

    private skipsInARow: number = 0;

    private skipping: boolean;

    private bonusCount: Map<number, number> = new Map<number, number>();
    private symbolIdCount: Map<number, number> = new Map<number, number>();
    private signals: SignalBinding[];

    private anticipationSignalBinding: SignalBinding;

    public onEnter() {
        this.bonusCount = new Map<number, number>();
        this.symbolIdCount = new Map<number, number>();

        this.skipping = false;

        if (Components.matrix.getTransition().canSkip()) {
            SmartUIFlags.set(SmartUIFlag.NO_SKIP, false);
        }

        this.attachLandSounds();

        const record: SpinRecord = GameState.getCurrentGame().getCurrentRecord() as SpinRecord;

        StopSpinCommand(record).then(() => this.complete());
    }

    public onSkip() {
        if (!this.skipping) {
            this.skipping = true;

            if (!SettingsModel.getTurboSpin() && Spin.ENABLE_TURBO_PROMPT) {
                this.skipsInARow++;
            }

            if (this.skipsInARow > Spin.TURBO_PROMPT_COUNT) {
                this.skipsInARow = 0;
                GameLoop.setPaused(true);
                Components.alert.confirm("trb.pro").then((turbo) => {
                    GameLoop.setPaused(false);
                    SettingsModel.setTurboSpin(turbo);
                    SmartUIManager.refresh();
                });
            }
        }

        if (Components.matrix.getTransition().canSkip()) {
            const record: SpinRecord = GameState.getCurrentGame().getCurrentRecord() as SpinRecord;
            SkipSpinCommand(record).then(() => this.skip());

            this.anticipationSignalBinding = Components.matrix.onAnticipationStart.add(() => {
                if (Components.matrix.getTransition().canSkip()) {
                    SmartUIFlags.set(SmartUIFlag.NO_SKIP, false);
                }
            });
        }

        SmartUIFlags.set(SmartUIFlag.NO_SKIP, true);
    }

    public onExit() {
        if (this.anticipationSignalBinding) {
            this.anticipationSignalBinding.detach();
            this.anticipationSignalBinding = null;
        }

        if (!this.skipping) {
            this.skipsInARow = 0;
        }

        SmartUIFlags.set(SmartUIFlag.NO_SKIP, false);

        this.detachLandSounds();

        if (Spin.ENABLE_SAFETY_CHECK) {
            const brokenSymbols = [];
            for (const symbol of Components.matrix.getBaseGridSymbols()) {
                const record = GameState.getCurrentGame().getCurrentRecord() as SpinRecord;
                if (symbol.gridPosition.y > 0 && symbol.gridPosition.y < record.grid.length) {
                    const actualSymbol = record.grid[symbol.gridPosition.x - 1][symbol.gridPosition.y - 1];
                    const expectedSymbol = symbol.symbolId;
                    if (symbol.symbolId !== actualSymbol) {
                        Logger.error("-" + expectedSymbol, "+" + actualSymbol, symbol.gridPosition, record.stops[symbol.gridPosition.x - 1]);
                        brokenSymbols.push(symbol);
                    }
                }
            }
            if (brokenSymbols.length) {
                Components.alert.error("Internal Error, please refresh", true).execute();
                Logger.error(brokenSymbols);
            }
        }
    }

    protected attachLandSounds() {
        this.signals = Components.matrix.getBaseGridSymbols().map((symbol) => {
            return symbol.landSignal.addOnce(() => this.checkForSymbolSounds(symbol));
        });
    }

    protected detachLandSounds() {
        for (const binding of this.signals) {
            binding.detach();
        }
    }

    private checkForSymbolSounds(symbolComponent: SymbolComponent) {
        const symbolDef = SlotDefinition.getSymbolDefinition(symbolComponent.symbolId);

        if (symbolDef.isBonusSymbol()) {
            this.checkForBonusSymbolSounds(symbolDef, symbolComponent);
        } else {
            this.playSymbolSounds(symbolDef, symbolComponent);
        }
    }

    private incrementCounter(counter: Map<number, number>, key: number) {
        if (!counter.has(key)) {
            counter.set(key, 0);
        }

        const currentCount = counter.get(key) + 1;
        counter.set(key, currentCount);

        return currentCount;
    }

    private playSymbolSounds(symbolDef: SymbolDefinition, symbolComponent: SymbolComponent) {
        const currentCount = this.incrementCounter(this.symbolIdCount, +symbolDef.id);
        Services.sound.event(SoundEvent.symbol_ID_land_COUNT, symbolDef.id, currentCount.toString());
    }

    private checkForBonusSymbolSounds(symbolDef: SymbolDefinition, symbolComponent: SymbolComponent) {
        const bonusSymbols = [symbolDef];

        // Check if wild counts towards bonuses, prioritise that sound
        if (symbolDef.wild) {
            SlotDefinition.symbolDefinitions.forEach((bonusSymbolDef) => {
                if (bonusSymbolDef.includeWilds && bonusSymbolDef.bonusId) {
                    bonusSymbols.unshift(bonusSymbolDef);
                }
            });
        }

        for (const bonusSymbolDef of bonusSymbols) {
            const currentReel = symbolComponent.gridPosition.x - 1;
            let currentCount = this.bonusCount.get(bonusSymbolDef.bonusId) || 0;

            // Symbols must be sequential in ways pays
            if (bonusSymbolDef.ways && currentCount < currentReel) {
                continue;
            }

            currentCount = this.incrementCounter(this.bonusCount, bonusSymbolDef.bonusId);

            let bonusTriggerPossible = false;

            if (currentCount >= bonusSymbolDef.matchesMin) {
                bonusTriggerPossible = true;
            } else {
                let remainingBonusReels = 0;

                for (let reel = currentReel + 1; reel <= SlotDefinition.matrixGrid.length; reel++) {
                    if (symbolDef.reels && symbolDef.reels.indexOf(reel + 1) >= 0) {
                        remainingBonusReels++;
                    }
                }

                if (currentCount + remainingBonusReels >= bonusSymbolDef.matchesMin) {
                    bonusTriggerPossible = true;
                }
            }

            if (bonusTriggerPossible) {
                Services.sound.event(SoundEvent.bonus_SYMBOL_land, symbolDef.id);
                Services.sound.event(SoundEvent.bonus_SYMBOL_land_COUNT, symbolDef.id, currentCount.toString());
                Services.sound.event(SoundEvent.bonus_ID_land, bonusSymbolDef.bonusId.toString());
                Services.sound.event(SoundEvent.bonus_ID_land_COUNT, bonusSymbolDef.bonusId.toString(), currentCount.toString());
                Services.sound.event(SoundEvent.bonus_ID_SYMBOL_land, bonusSymbolDef.bonusId.toString(), symbolDef.id);
                Services.sound.event(SoundEvent.bonus_ID_SYMBOL_land_COUNT, bonusSymbolDef.bonusId.toString(), symbolDef.id, currentCount.toString());
                return;
            }
        }
    }
}
